import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Badge } from 'react-bootstrap';

const BASE_URL = 'https://hr-service-git-618063327581.asia-southeast2.run.app';
const CARD_WIDTH = 240;
const CARD_GAP = 14;
const VISIBLE = 3;

export default function AnnouncementSlider({ title, announcements, accentColor = '#3B82F6', icon = 'general' }) {
    const [idx, setIdx] = useState(0);
    const [selected, setSelected] = useState(null);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    const maxIdx = Math.max(0, announcements.length - VISIBLE);

    const next = useCallback(() => setIdx(i => (i >= maxIdx ? 0 : i + 1)), [maxIdx]);
    const prev = () => setIdx(i => (i <= 0 ? maxIdx : i - 1));

    useEffect(() => {
        if (paused || announcements.length <= 1) return;
        timerRef.current = setInterval(next, 5000);
        return () => clearInterval(timerRef.current);
    }, [paused, announcements.length, next]);

    if (!announcements || !announcements.length) return null;

    const IconComp = icon === 'urgent' ? 'fe-alert-triangle' : 'fe-bell';

    return (
        <div style={{ marginBottom: 32 }}>
            <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center justify-content-center br-7 shadow-sm" style={{
                        width: 30, height: 30,
                        background: accentColor + '15',
                    }}>
                        <i className={`fe ${IconComp}`} style={{ color: accentColor, fontSize: 14 }}></i>
                    </div>
                    <span className="fw-bold text-dark-emphasis" style={{ fontSize: 15 }}>{title}</span>
                    <Badge pill bg="light" text="muted" className="border fw-normal">{announcements.length}</Badge>
                </div>

                {announcements.length > VISIBLE && (
                    <div className="d-flex gap-2">
                        <button onClick={prev} style={arrowStyle(idx === 0)}>
                            <i className="fe fe-chevron-left"></i>
                        </button>
                        <button onClick={next} style={arrowStyle(idx >= maxIdx)}>
                            <i className="fe fe-chevron-right"></i>
                        </button>
                    </div>
                )}
            </div>

            <div style={{ overflow: 'hidden', padding: '10px 4px' }} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
                <div style={{
                    display: 'flex', gap: CARD_GAP,
                    transform: `translateX(-${idx * (CARD_WIDTH + CARD_GAP)}px)`,
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                    {announcements.map((ann) => (
                        <AnnouncementCard key={ann.id} ann={ann} accentColor={accentColor} onClick={() => setSelected(ann)} />
                    ))}
                </div>
            </div>

            {announcements.length > VISIBLE && (
                <div className="d-flex justify-content-center gap-2 mt-2">
                    {Array.from({ length: maxIdx + 1 }).map((_, i) => (
                        <div key={i} onClick={() => setIdx(i)}
                            style={{
                                width: i === idx ? 16 : 6, height: 6,
                                borderRadius: 3, cursor: 'pointer',
                                background: i === idx ? accentColor : '#E5E7EB',
                                transition: 'all 0.3s'
                            }} />
                    ))}
                </div>
            )}

            <AnnouncementModal ann={selected} onClose={() => setSelected(null)} accentColor={accentColor} />
        </div>
    );
}

function AnnouncementCard({ ann, accentColor, onClick }) {
    const imgSrc = ann.gambar_path ? `${BASE_URL}${ann.gambar_path.startsWith('/') ? '' : '/'}${ann.gambar_path}` : null;
    const isUrgent = ann.tipe === 'urgent';
    const formattedDate = new Date(ann.created_at || ann.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' });

    return (
        <div onClick={onClick}
            className="announcement-card-hover"
            style={{
                minWidth: CARD_WIDTH, width: CARD_WIDTH,
                borderRadius: 12, overflow: 'hidden',
                border: `1px solid ${isUrgent ? '#FEE2E2' : '#F3F4F6'}`,
                cursor: 'pointer', background: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                flexShrink: 0,
            }}>
            
            <div style={{ height: 120, background: '#F9FAFB', position: 'relative', overflow: 'hidden' }}>
                {imgSrc && (
                    <img src={imgSrc} alt={ann.judul} 
                         style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                         onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }} />
                )}
                <div style={{
                    display: imgSrc ? 'none' : 'flex',
                    height: '100%', alignItems: 'center', justifyContent: 'center',
                    background: isUrgent ? `linear-gradient(135deg, #FEF2F2, #FEE2E2)` : `linear-gradient(135deg, #F0F9FF, #E0F2FE)`
                }}>
                    <i className={`fe ${isUrgent ? 'fe-alert-triangle' : 'fe-bell'}`} style={{ fontSize: 32, color: isUrgent ? '#EF4444' : '#3B82F6', opacity: 0.6 }}></i>
                </div>
                {isUrgent && (
                    <div style={{
                        position: 'absolute', top: 10, right: 10,
                        background: '#EF4444', color: '#fff',
                        borderRadius: 4, padding: '2px 8px', fontSize: 10, fontWeight: 700,
                        boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
                    }}>URGENT</div>
                )}
            </div>

            <div style={{ padding: '12px' }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                    <span style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 500 }}>{formattedDate}</span>
                    {ann.company?.nama && (
                        <Badge bg="light" text="dark" className="border fs-10 fw-normal rounded-pill px-2">{ann.company.nama}</Badge>
                    )}
                </div>
                <div className="fw-semibold text-dark" style={{ fontSize: 13, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.8em' }}>
                    {ann.judul}
                </div>
            </div>
            <style>{`
                .announcement-card-hover { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                .announcement-card-hover:hover { transform: translateY(-4px); box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important; border-color: ${accentColor}40 !important; }
            `}</style>
        </div>
    );
}

function AnnouncementModal({ ann, onClose, accentColor }) {
    const [slideIdx, setSlideIdx] = useState(0);
    
    useEffect(() => {
        if (!ann) setSlideIdx(0);
    }, [ann]);

    if (!ann) return null;

    const isUrgent = ann.tipe === 'urgent';
    const attachments = ann.attachments || [];

    const imageItems = [
        ...(ann.gambar_path ? [{ id: 'main', url: `${BASE_URL}${ann.gambar_path.startsWith('/') ? '' : '/'}${ann.gambar_path}`, name: 'Gambar Utama' }] : []),
        ...attachments
            .filter(a => a.file_type?.includes('image'))
            .map(a => ({ id: a.id, url: `${BASE_URL}${a.file_path.startsWith('/') ? '' : '/'}${a.file_path}`, name: a.file_name }))
    ];
    const fileItems = attachments.filter(a => !a.file_type?.includes('image'));

    const goSlide = (dir) => {
        setSlideIdx(i => {
            const next = i + dir;
            if (next < 0) return imageItems.length - 1;
            if (next >= imageItems.length) return 0;
            return next;
        });
    };

    const getFileIcon = (fileType) => {
        if (!fileType) return 'fe-file';
        if (fileType.includes('pdf')) return 'fe-file-text text-danger';
        if (fileType.includes('word') || fileType.includes('msword')) return 'fe-file-text text-primary';
        if (fileType.includes('excel') || fileType.includes('sheet')) return 'fe-file-text text-success';
        return 'fe-paperclip';
    };

    const formattedDateFull = new Date(ann.created_at || ann.createdAt).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

    return (
        <Modal show={!!ann} onHide={onClose} centered size="lg" className="border-0">
            {imageItems.length > 0 && (
                <div style={{ height: 320, overflow: 'hidden', position: 'relative', background: '#000' }}>
                    <div style={{
                        display: 'flex', width: `${imageItems.length * 100}%`, height: '100%',
                        transform: `translateX(-${slideIdx * (100 / imageItems.length)}%)`, transition: 'transform 0.4s ease'
                    }}>
                        {imageItems.map(img => (
                            <img key={img.id} src={img.url} alt={img.name} style={{ width: `${100 / imageItems.length}%`, height: '100%', objectFit: 'cover', flexShrink: 0 }} />
                        ))}
                    </div>

                    {imageItems.length > 1 && (
                        <>
                            <button onClick={() => goSlide(-1)} style={sliderBtnStyle('left')}>
                                <i className="fe fe-chevron-left"></i>
                            </button>
                            <button onClick={() => goSlide(1)} style={sliderBtnStyle('right')}>
                                <i className="fe fe-chevron-right"></i>
                            </button>
                            <div className="d-flex gap-2 position-absolute" style={{ bottom: 10, left: '50%', transform: 'translateX(-50%)' }}>
                                {imageItems.map((_, i) => (
                                    <div key={i} onClick={() => setSlideIdx(i)}
                                        style={{
                                            width: i === slideIdx ? 18 : 6, height: 6,
                                            borderRadius: 3, cursor: 'pointer',
                                            background: i === slideIdx ? '#fff' : 'rgba(255,255,255,0.5)',
                                            transition: 'all 0.3s'
                                        }} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            <Modal.Header closeButton className={imageItems.length > 0 ? "border-0 pt-4" : ""} style={{ paddingBottom: 0 }}>
                <Modal.Title className="fw-bold w-100">
                    <div className="d-flex gap-2 mb-2">
                        {isUrgent && <Badge bg="danger" className="rounded-pill px-3">URGENT</Badge>}
                        {ann.company?.nama && <Badge bg="primary" className="rounded-pill px-3">{ann.company.nama}</Badge>}
                    </div>
                    <h4 className="mb-0 fw-bold" style={{ color: '#111827' }}>{ann.judul}</h4>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 pb-4">
                <div className="d-flex gap-3 mb-4 text-muted" style={{ fontSize: 13 }}>
                    <span><i className="fe fe-calendar me-1"></i> {formattedDateFull}</span>
                    {ann.creator?.nama_lengkap && (
                        <span><i className="fe fe-user me-1"></i> {ann.creator.nama_lengkap}</span>
                    )}
                </div>

                <hr className="bg-light" />

                <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 8 }}>
                    <p style={{ fontSize: 14, lineHeight: 1.7, color: '#374151', whiteSpace: 'pre-wrap', margin: 0 }}>
                        {ann.konten}
                    </p>
                </div>

                {fileItems.length > 0 && (
                    <>
                        <hr className="bg-light my-4" />
                        <div className="mb-3 d-flex align-items-center gap-2">
                            <i className="fe fe-paperclip text-muted"></i>
                            <span className="fw-bold" style={{ fontSize: 13 }}>Lampiran ({fileItems.length})</span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            {fileItems.map(att => (
                                <a key={att.id} href={`${BASE_URL}${att.file_path.startsWith('/') ? '' : '/'}${att.file_path}`}
                                    target="_blank" rel="noreferrer"
                                    className="d-flex align-items-center gap-2 px-3 py-2 rounded text-decoration-none text-dark border"
                                    style={{ background: '#F9FAFB', transition: 'background 0.2s', fontSize: 13 }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                                    onMouseLeave={e => e.currentTarget.style.background = '#F9FAFB'}>
                                    <i className={`fe ${getFileIcon(att.file_type)} fs-5`}></i>
                                    <span className="flex-grow-1 text-truncate">{att.file_name}</span>
                                    {att.file_size && (
                                        <span className="text-muted" style={{ fontSize: 11 }}>
                                            {(att.file_size / 1024).toFixed(0)} KB
                                        </span>
                                    )}
                                </a>
                            ))}
                        </div>
                    </>
                )}
            </Modal.Body>
        </Modal>
    );
}

function arrowStyle(disabled) {
    return {
        width: 30, height: 30, borderRadius: 8, border: '1px solid #E5E7EB',
        background: disabled ? '#F9FAFB' : '#fff', color: disabled ? '#D1D5DB' : '#374151',
        cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, transition: 'all 0.2s'
    };
}

function sliderBtnStyle(side) {
    return {
        position: 'absolute', top: '50%', transform: 'translateY(-50%)', [side]: 10,
        width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.35)', color: '#fff',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 16, zIndex: 2, transition: 'background 0.2s'
    };
}
